import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Input, Card, CardContent, Spinner } from "@learn-me/ui";
import { ArrowLeft, Save, X, Plus } from "lucide-react";
import toast from "react-hot-toast";
import { OFFERING_FOCUS_LABELS, type OfferingCategory } from "@learn-me/types";
import { getErrorMessage } from "@/lib/api";
import { offeringsApi } from "@/features/offerings/api/offeringsApi";

interface FormValues {
  category: OfferingCategory;
  title: string;
  summary: string;
  durationLabel: string;
  isPublished: boolean;
}

const MODALITY_OPTIONS = [
  "Presencial",
  "Virtual sincrónica",
  "Combinada",
  "Adaptable a virtual",
];

/** Campo de lista editable: las áreas clave y las modalidades son arreglos */
function TagInput({
  label,
  hint,
  values,
  onChange,
  suggestions,
}: {
  label: string;
  hint?: string;
  values: string[];
  onChange: (values: string[]) => void;
  suggestions?: string[];
}) {
  const [draft, setDraft] = useState("");

  const add = (value: string) => {
    const clean = value.trim();
    if (!clean || values.includes(clean)) return;
    onChange([...values, clean]);
    setDraft("");
  };

  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {hint && <p className="text-xs text-muted-foreground mb-2">{hint}</p>}

      <div className="flex flex-wrap gap-2 mb-2">
        {values.map((value) => (
          <span
            key={value}
            className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-50 text-primary-700 text-sm rounded-full"
          >
            {value}
            <button
              type="button"
              aria-label={`Quitar ${value}`}
              onClick={() => onChange(values.filter((v) => v !== value))}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add(draft);
            }
          }}
          placeholder="Escriba y presione Enter"
          className="flex-1 px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <Button type="button" variant="outline" onClick={() => add(draft)}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {suggestions && suggestions.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {suggestions
            .filter((s) => !values.includes(s))
            .map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => add(suggestion)}
                className="text-xs px-2 py-1 rounded-full border border-secondary-200 text-secondary-600 hover:border-secondary-400"
              >
                + {suggestion}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

export default function OfferingEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [focusAreas, setFocusAreas] = useState<string[]>([]);
  const [modalities, setModalities] = useState<string[]>([]);
  const [listError, setListError] = useState<string | null>(null);

  const { data: offering, isLoading } = useQuery({
    queryKey: ["admin-offering", id],
    queryFn: () => offeringsApi.getById(id!),
    enabled: isEditing,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      category: "PROGRAMA",
      isPublished: false,
    },
  });

  useEffect(() => {
    if (!offering) return;
    reset({
      category: offering.category,
      title: offering.title,
      summary: offering.summary,
      durationLabel: offering.durationLabel,
      isPublished: offering.isPublished,
    });
    setFocusAreas(offering.focusAreas);
    setModalities(offering.modalities);
  }, [offering, reset]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-offerings"] });
    queryClient.invalidateQueries({ queryKey: ["offerings"] });
  };

  const saveMutation = useMutation({
    mutationFn: (values: FormValues) => {
      const payload = {
        ...values,
        focusAreas,
        modalities,
      };
      return isEditing
        ? offeringsApi.update(id!, payload)
        : offeringsApi.create(payload);
    },
    onSuccess: () => {
      invalidate();
      toast.success(isEditing ? "Programa actualizado" : "Programa creado");
      navigate("/admin/programas");
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const onSubmit = (values: FormValues) => {
    if (focusAreas.length === 0) {
      setListError("Agregue al menos un área o enfoque");
      return;
    }
    if (modalities.length === 0) {
      setListError("Indique al menos una modalidad");
      return;
    }
    setListError(null);
    saveMutation.mutate(values);
  };

  const category = watch("category");

  if (isEditing && isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl">
      <Link
        to="/admin/programas"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver
      </Link>

      <h1 className="text-2xl font-bold mb-6">
        {isEditing ? "Editar programa" : "Nuevo programa"}
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
        <Card>
          <CardContent className="p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium mb-1">Tipo</label>
              <select
                className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                {...register("category", { required: true })}
              >
                <option value="PROGRAMA">Programa</option>
                <option value="CAPACITACION">Capacitación</option>
              </select>
            </div>

            <Input
              label="Título"
              placeholder="Liderazgo y gestión de equipos"
              error={errors.title?.message}
              {...register("title", {
                required: "El título es requerido",
                minLength: { value: 3, message: "Mínimo 3 caracteres" },
              })}
            />

            <div>
              <label className="block text-sm font-medium mb-1">
                Descripción
              </label>
              <textarea
                rows={5}
                placeholder="Qué logra la organización con este programa"
                className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                {...register("summary", {
                  required: "La descripción es requerida",
                  minLength: { value: 20, message: "Mínimo 20 caracteres" },
                })}
              />
              {errors.summary && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.summary.message}
                </p>
              )}
            </div>

            <Input
              label="Duración"
              placeholder="16 horas, o 16 a 24 horas"
              error={errors.durationLabel?.message}
              {...register("durationLabel", {
                required: "La duración es requerida",
              })}
            />
            <p className="text-xs text-muted-foreground -mt-3">
              Se escribe tal cual aparecerá en la página, incluidos los rangos.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 space-y-6">
            <TagInput
              label={OFFERING_FOCUS_LABELS[category] ?? "Áreas clave"}
              hint="Se muestran como etiquetas en la tarjeta y el detalle."
              values={focusAreas}
              onChange={setFocusAreas}
            />

            <TagInput
              label="Modalidades"
              values={modalities}
              onChange={setModalities}
              suggestions={MODALITY_OPTIONS}
            />

            {listError && <p className="text-sm text-red-600">{listError}</p>}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                {...register("isPublished")}
              />
              <span className="font-medium">Publicado</span>
            </label>
            <p className="text-xs text-muted-foreground mt-1 ml-6">
              Solo los programas publicados aparecen en la página pública.
            </p>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button type="submit" disabled={saveMutation.isPending}>
            <Save className="h-4 w-4 mr-2" />
            {saveMutation.isPending ? "Guardando..." : "Guardar"}
          </Button>
          <Link to="/admin/programas">
            <Button type="button" variant="outline">
              Cancelar
            </Button>
          </Link>
        </div>
      </form>
    </div>
  );
}
